import yaml from 'js-yaml';
import type { GameData, Location, AnomalyData, ResearcherData, ResearchType, ResearchTypeId, SilverBullet, GameConfig, PropertyType, RealityModifier, PropertyHelperData, UIConstants, ResearchCost, GameEndData, Costs, InsanityConfig, OnePropertyNameEntry, TwoPropertyNameEntry } from '../game/types';

export class DataLoader {
  private static instance: DataLoader;
  private data: GameData | null = null;

  private constructor() {}

  static getInstance(): DataLoader {
    if (!DataLoader.instance) {
      DataLoader.instance = new DataLoader();
    }
    return DataLoader.instance;
  }

  async loadAll(): Promise<GameData> {
    if (this.data) {
      return this.data;
    }

    try {
      const [
        locationsData,
        anomaliesData,
        scientistsData,
        propertiesData,
        researchTypesData,
        silverBulletsData,
        gameConfigData,
        gameEndData,
        costsData,
        insanityData,
        uiConstantsData,
        uiStringsData,
        hotkeysData,
        realityModifiersData,
        howToPlayData,
        propertiesHelperData,
        tooltipsData,
        onePropertyNamesData,
        twoPropertyNamesData,
        objectivesData
      ] = await Promise.all([
        fetch('./data/locations.yaml').then(r => {
          if (!r.ok) throw new Error(`Failed to load locations.yaml: ${r.statusText}`);
          return r.text();
        }),
        fetch('./data/anomalies.yaml').then(r => {
          if (!r.ok) throw new Error(`Failed to load anomalies.yaml: ${r.statusText}`);
          return r.text();
        }),
        fetch('./data/scientists.yaml').then(r => {
          if (!r.ok) throw new Error(`Failed to load scientists.yaml: ${r.statusText}`);
          return r.text();
        }),
        fetch('./data/properties.yaml').then(r => {
          if (!r.ok) throw new Error(`Failed to load properties.yaml: ${r.statusText}`);
          return r.text();
        }),
        fetch('./data/research_types.yaml').then(r => {
          if (!r.ok) throw new Error(`Failed to load research_types.yaml: ${r.statusText}`);
          return r.text();
        }),
        fetch('./data/silver_bullets.yaml').then(r => {
          if (!r.ok) throw new Error(`Failed to load silver_bullets.yaml: ${r.statusText}`);
          return r.text();
        }),
        fetch('./data/game_config.yaml').then(r => {
          if (!r.ok) throw new Error(`Failed to load game_config.yaml: ${r.statusText}`);
          return r.text();
        }),
        fetch('./data/game_end.yaml').then(r => {
          if (!r.ok) throw new Error(`Failed to load game_end.yaml: ${r.statusText}`);
          return r.text();
        }),
        fetch('./data/costs.yaml').then(r => {
          if (!r.ok) throw new Error(`Failed to load costs.yaml: ${r.statusText}`);
          return r.text();
        }),
        fetch('./data/insanity.yaml').then(r => {
          if (!r.ok) throw new Error(`Failed to load insanity.yaml: ${r.statusText}`);
          return r.text();
        }),
        fetch('./data/ui_constants.yaml').then(r => {
          if (!r.ok) throw new Error(`Failed to load ui_constants.yaml: ${r.statusText}`);
          return r.text();
        }),
        fetch('./data/ui/strings.yaml').then(r => {
          if (!r.ok) throw new Error(`Failed to load strings.yaml: ${r.statusText}`);
          return r.text();
        }),
        fetch('./data/hotkeys.yaml').then(r => {
          if (!r.ok) throw new Error(`Failed to load hotkeys.yaml: ${r.statusText}`);
          return r.text();
        }),
        fetch('./data/reality_modifier.yaml').then(r => {
          if (!r.ok) throw new Error(`Failed to load reality_modifier.yaml: ${r.statusText}`);
          return r.text();
        }),
        fetch('./data/howtoplay.yaml').then(r => {
          if (!r.ok) throw new Error(`Failed to load howtoplay.yaml: ${r.statusText}`);
          return r.text();
        }),
        fetch('./data/properties_helper.yaml').then(r => {
          if (!r.ok) throw new Error(`Failed to load properties_helper.yaml: ${r.statusText}`);
          return r.text();
        }),
        fetch('./data/tooltips.yaml').then(r => {
          if (!r.ok) throw new Error(`Failed to load tooltips.yaml: ${r.statusText}`);
          return r.text();
        }),
        fetch('./data/one_property_names.yaml').then(r => {
          if (!r.ok) throw new Error(`Failed to load one_property_names.yaml: ${r.statusText}`);
          return r.text();
        }),
        fetch('./data/two_properties_names.yaml').then(r => {
          if (!r.ok) throw new Error(`Failed to load two_properties_names.yaml: ${r.statusText}`);
          return r.text();
        }),
        fetch('./data/objectives.yaml').then(r => {
          if (!r.ok) throw new Error(`Failed to load objectives.yaml: ${r.statusText}`);
          return r.text();
        })
      ]);

      const locations = yaml.load(locationsData) as { locations: Location[] };
      const anomalies = yaml.load(anomaliesData) as { anomalies: AnomalyData[] };
      const scientists = yaml.load(scientistsData) as { scientists: ResearcherData[] };
      const properties = yaml.load(propertiesData) as { properties: Array<{ name: PropertyType; symbol: string; color?: string }> };
      const researchTypes = yaml.load(researchTypesData) as { research_types: Array<{ name: string; costs: unknown[] }> };
      const silverBullets = yaml.load(silverBulletsData) as { silver_bullets: SilverBullet[] };
      const gameConfig = yaml.load(gameConfigData) as { game_config: GameConfig };
      const gameEnd = yaml.load(gameEndData) as { game_end: GameEndData };
      const costs = yaml.load(costsData) as { costs: Costs };
      const insanity = yaml.load(insanityData) as { insanity: InsanityConfig };
      const uiConstants = yaml.load(uiConstantsData) as { ui_constants: UIConstants };
      const uiStrings = yaml.load(uiStringsData) as { ui_strings: Record<string, unknown> };
      const hotkeys = yaml.load(hotkeysData) as { hotkeys: Record<string, unknown> };
      const realityModifiersRaw = yaml.load(realityModifiersData) as { reality_modifiers: RealityModifier[]; reality_modifiers_end: RealityModifier[] };
      const howToPlay = yaml.load(howToPlayData) as { how_to_play: Record<string, unknown> };
      const propertiesHelper = yaml.load(propertiesHelperData) as { properties_helper: PropertyHelperData[] };
      const tooltips = yaml.load(tooltipsData) as { tooltips: Record<string, unknown> };
      const onePropertyNames = yaml.load(onePropertyNamesData) as { one_property: OnePropertyNameEntry[] };
      const twoPropertyNames = yaml.load(twoPropertyNamesData) as { two_properties: TwoPropertyNameEntry[] };
      const objectives = yaml.load(objectivesData) as { objectives: Record<string, string> };

      // Validate loaded data
      if (!locations.locations || !Array.isArray(locations.locations)) {
        throw new Error('Invalid locations data: missing or invalid locations array');
      }
      if (!anomalies.anomalies || !Array.isArray(anomalies.anomalies)) {
        throw new Error('Invalid anomalies data: missing or invalid anomalies array');
      }
      if (!scientists.scientists || !Array.isArray(scientists.scientists)) {
        throw new Error('Invalid scientists data: missing or invalid scientists array');
      }
      if (!gameConfig.game_config) {
        throw new Error('Invalid game config data: missing game_config');
      }
      if (!gameEnd.game_end) {
        throw new Error('Invalid game end data: missing game_end');
      }
      if (!costs.costs) {
        throw new Error('Invalid costs data: missing costs');
      }
      if (!insanity.insanity) {
        throw new Error('Invalid insanity data: missing insanity');
      }
      if (!uiConstants.ui_constants) {
        throw new Error('Invalid UI constants data: missing ui_constants');
      }
      if (!objectives.objectives || typeof objectives.objectives !== 'object') {
        throw new Error('Invalid objectives data: missing or invalid objectives');
      }

      // Transform properties: extract names for compatibility and create symbol/color mappings
      const propertyNames = properties.properties.map(p => p.name);
      const propertySymbols: Record<string, string> = {};
      const propertyColors: Record<string, string> = {};
      properties.properties.forEach(p => {
        propertySymbols[p.name.toLowerCase()] = p.symbol;
        if (p.color) {
          propertyColors[p.name.toLowerCase()] = p.color;
        }
      });

      // Transform research types to match our interface
      // YAML structure: each item has a 'name' property, then costs
      const transformedResearchTypes: ResearchType[] = researchTypes.research_types.map((rt) => {
        if (!rt.name || typeof rt.name !== 'string') {
          throw new Error(`Invalid research type structure: missing 'name' property in ${JSON.stringify(rt)}`);
        }
        const id = rt.name as ResearchTypeId;
        return {
          id,
          costs: Array.isArray(rt.costs) ? rt.costs as ResearchCost[] : [],
          risks: [] // Risks removed - effects are handled through property mechanics
        };
      });

      // Add property symbols and colors to UI strings
      const enhancedUiStrings = {
        ...uiStrings.ui_strings,
        propertySymbols,
        propertyColors
      };

      this.data = {
        locations: locations.locations,
        anomalies: anomalies.anomalies,
        researchers: scientists.scientists,
        properties: propertyNames,
        researchTypes: transformedResearchTypes,
        silverBullets: silverBullets.silver_bullets,
        realityModifiers: realityModifiersRaw.reality_modifiers || [],
        realityModifiersEnd: realityModifiersRaw.reality_modifiers_end || [],
        gameConfig: gameConfig.game_config,
        gameEndData: gameEnd.game_end,
        costs: costs.costs,
        insanityConfig: insanity.insanity,
        uiConstants: uiConstants.ui_constants,
        uiStrings: enhancedUiStrings,
        hotkeys: hotkeys.hotkeys,
        howToPlay: howToPlay.how_to_play,
        propertiesHelper: propertiesHelper.properties_helper,
        tooltips: tooltips.tooltips,
        onePropertyNames: onePropertyNames.one_property || [],
        twoPropertyNames: twoPropertyNames.two_properties || [],
        objectives: objectives.objectives
      };

      return this.data;
    } catch (error) {
      if (error instanceof Error) {
        throw new Error(`Failed to load game data: ${error.message}`);
      }
      throw new Error('Failed to load game data: Unknown error');
    }
  }

  getData(): GameData {
    if (!this.data) {
      throw new Error('Data not loaded. Call loadAll() first.');
    }
    return this.data;
  }
}

